import {
  Alert,
  Dialog,
  Heading,
  List,
  Paragraph,
} from "@digdir/designsystemet-react";
import { useTranslation } from "react-i18next";
import type { ValidationProblemDetails } from "@/api/generated";

type ValidationProblemsProps = {
  validationProblemDetails: ValidationProblemDetails;
  maxErrorsToShow?: number;
  title?: string;
  message?: string;
};

export const ValidationProblems = ({
  validationProblemDetails,
  maxErrorsToShow = 5,
  message,
}: ValidationProblemsProps) => {
  const { t } = useTranslation();

  const errors = Object.entries(validationProblemDetails.errors ?? {});

  const numberOfErrors = errors.reduce(
    (acc: number, curr) => acc + curr[1].length,
    0,
  );

  const topK = errors.slice(0, Math.min(maxErrorsToShow, errors.length));

  const showMore = numberOfErrors > topK.length;

  return (
    <Alert data-color="warning">
      <Heading level={3}>
        {t("valideringsProblem.tittel", { context: numberOfErrors.toString() })}
      </Heading>
      {message && <Paragraph className="mb-4">{message}</Paragraph>}
      <List.Unordered>
        {topK.map((e) => {
          return <ValidationError key={e[0]} error={e} />;
        })}
      </List.Unordered>
      {showMore && (
        <Dialog.TriggerContext>
          <Dialog.Trigger className="mt-4" variant="secondary">
            {t("valideringsProblem.vis-mer")}
          </Dialog.Trigger>
          <Dialog closedby="any" className="bg-balder-background-tinted">
            <Heading
              style={{
                marginBottom: "var(--ds-size-2)",
              }}
            >
              {t("valideringsProblem.dialog-tittel")}
            </Heading>
            <List.Unordered>
              {errors.map((e) => {
                return <ValidationError key={e[0]} error={e} />;
              })}
            </List.Unordered>
          </Dialog>
        </Dialog.TriggerContext>
      )}
    </Alert>
  );
};

const ValidationError = ({ error }: { error: [string, string[]] }) => {
  const title = error[0];
  const messages = error[1];
  return (
    <List.Item key={error[0]}>
      <span>{title}</span>
      <List.Unordered>
        {messages.map((msg) => (
          <List.Item key={msg}>{msg}</List.Item>
        ))}
      </List.Unordered>
    </List.Item>
  );
};
